import { useLayoutEffect, useRef } from 'react'

/** What had focus inside a node when its slot let it go, to give back once the next slot holds it. */
const focusInside = new WeakMap<HTMLElement, HTMLElement>()

/** Where something inside the node was scrolled to, and whether to its end. */
interface ScrollMark {
  element: Element
  top: number
  left: number
  atEnd: boolean
}

/** What was scrolled inside a node when its slot let it go: taking an element out of the document drops its offsets. */
const scrollInside = new WeakMap<HTMLElement, ScrollMark[]>()

/** The scrolled elements in `node` (itself included), with their offsets. */
function scrollMarks(node: HTMLElement): ScrollMark[] {
  const marks: ScrollMark[] = []
  for (const element of [node, ...node.querySelectorAll('*')]) {
    const { scrollTop: top, scrollLeft: left, scrollHeight, clientHeight } = element
    if (top > 0 || left > 0) marks.push({ element, top, left, atEnd: scrollHeight - top - clientHeight <= 1 })
  }
  return marks
}

/** Scrolls each element back: one that was at its end goes to its end in its new size (a chat following the newest). */
function restoreScroll(marks: ScrollMark[]) {
  for (const { element, top, left, atEnd } of marks) {
    element.scrollTop = atEnd ? element.scrollHeight : top
    element.scrollLeft = left
  }
}

/**
 * Holds a node from `useMovableNode` while mounted. One slot holds a node at a time; when the layout changes, the old
 * slot lets go and the new one takes it in the same commit, so the node is never out of the document when the browser
 * next looks (a video taken out of the document pauses, and wouldn't start again by itself). Focus inside the node and
 * what was scrolled in it (a chat's log) survive the move.
 *
 * Within that commit the node is briefly detached: the old slot lets go while React applies the commit's changes, and
 * the new slot takes it in its own layout effect, after those of the components before it in the tree. A layout effect
 * in the portal's content that runs during a move (one keyed on the layout) can see its node out of the document, with
 * no size: check `isConnected` and leave measuring to a ResizeObserver or a later effect.
 */
export function MovableSlot({ node, className }: { node: HTMLElement; className?: string }) {
  const slotRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const slot = slotRef.current
    if (!slot) return
    const focused = focusInside.get(node)
    const scrolled = scrollInside.get(node)
    focusInside.delete(node)
    scrollInside.delete(node)
    slot.appendChild(node)
    if (scrolled) restoreScroll(scrolled)
    if (focused && node.contains(focused) && document.activeElement !== focused) focused.focus({ preventScroll: true })
    return () => {
      if (node.parentNode !== slot) return
      const active = document.activeElement
      if (active instanceof HTMLElement && node.contains(active)) focusInside.set(node, active)
      scrollInside.set(node, scrollMarks(node))
      slot.removeChild(node)
    }
  }, [node])
  return <div ref={slotRef} className={className} />
}
